// src/app/models/filter.interface.ts
export interface FilterValue {
  value: string;
  count: number;
}

export interface DynamicFilter {
  name: string;
  values: FilterValue[];
  filter_select_option: string;
}
export interface FilterOption {
  label: string;
  value: any;
  count: number;
  fieldType?: 'category' | 'sex' | 'color';
  isNotSpecified?: boolean;
}
export interface FilterOutput {
  [key: string]: string[] | { latitude: any; longitude: any }[];
}
export interface LocationValue {
  latitude: number;
  longitude: number;
  address: string;
}

export interface FilterField {
  label: string;
  key: string;
  type: 'select' | 'multiselect' | 'text';
  options: FilterOption[];
  value?: FilterOption | FilterOption[] | string | null;
  disabled?: boolean;
}

export function transformDynamicFilters(
  filters: DynamicFilter[],
  currentFilters: { [key: string]: string[] } = {}
): FilterField[] {
  return filters.map((filter) => {
    // Create the base field object
    const field: FilterField = {
      label: filter.name,
      key: filter.name,
      options: [],
      value: [],
      type:
        filter.filter_select_option === 'multi_select'
          ? 'multiselect'
          : 'select',
    };

    // Transform the options
    const mappedOptions: FilterOption[] = filter.values
      .map((v): FilterOption | null => {
        // Handle empty or invalid values
        if (!v || v.value === undefined || v.value === null) {
          return null;
        }

        // Location handling
        if (filter.name === 'Location' && v.value) {
          try {
            const locationData: LocationValue = JSON.parse(v.value);

            return {
              label: locationData.address,
              value: {
                latitude: locationData.latitude,
                longitude: locationData.longitude,
              },
              count: v.count,
            };
          } catch (e) {
            console.error('Error parsing location data:', e);
            return null;
          }
        }

        // Boolean handling
        if (v.value === 'true' || v.value === 'false') {
          return {
            label: v.value === 'true' ? 'Yes' : 'No',
            value: v.value === 'true',
            count: v.count,
          };
        }

        // Date handling
        if (filter.name === 'Date Of Birth' || filter.name === 'Last Mated') {
          const date = new Date(v.value);
          if (!isNaN(date.getTime())) {
            return {
              label: new Intl.DateTimeFormat('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
              }).format(date),
              value: v.value,
              count: v.count,
            };
          }
          return null;
        }

        // Special handling for numeric values
        if (!isNaN(Number(v.value))) {
          const numValue = Number(v.value);
          return {
            label: `${numValue}`,
            value: numValue, // Store as number
            count: v.count,
          };
        }

        // Default string handling
        return {
          label: v.value,
          value: v.value,
          count: v.count,
        };
      })
      .filter((option): option is FilterOption => option !== null);

    // Set the options
    field.options = mappedOptions;

    // Handle selected values from currentFilters
    if (currentFilters[filter.name]) {
      const selectedValues = currentFilters[filter.name];
      field.value = mappedOptions.filter((option) => {
        return selectedValues.some((selectedValue) => {
          // Handle numeric comparisons
          if (typeof option.value === 'number') {
            return Number(selectedValue) === option.value;
          }
          return String(selectedValue) === String(option.value);
        });
      });
    }

    return field;
  });
}

// Static Filters Types
export interface StaticFilterItem {
  sex?: string;
  category?: string;
  color?: string | null;
  count: number;
}

export interface StaticFilters {
  sexes?: { sex: string; count: number }[];
  categories?: { category: string; count: number }[];
  colors?: { color: string | null; count: number }[];
}

// Static filter configuration - maps API key to filter config
const STATIC_FILTER_CONFIG: Record<
  string,
  {
    label: string;
    valueKey: string;
    paramKey: string;
    fieldType: 'category' | 'sex' | 'color';
  }
> = {
  sexes: { label: 'Sex', valueKey: 'sex', paramKey: 'sex', fieldType: 'sex' },
  categories: {
    label: 'Category',
    valueKey: 'category',
    paramKey: 'category',
    fieldType: 'category',
  },
  colors: {
    label: 'Color',
    valueKey: 'color',
    paramKey: 'color',
    fieldType: 'color',
  },
};

export function transformStaticFilters(
  filters: StaticFilters,
  currentFilters: { [key: string]: string[] } = {}
): FilterField[] {
  const filterFields: FilterField[] = [];

  // Add name search field FIRST (text input)
  filterFields.push({
    label: 'Name',
    key: 'search_term',
    type: 'text',
    options: [],
    value: currentFilters['search_term']?.[0] || '',
  });

  // Always include all static filters in order: sex, category, color
  const filterOrder = ['sexes', 'categories', 'colors'];

  filterOrder.forEach((apiKey) => {
    const config = STATIC_FILTER_CONFIG[apiKey];
    if (!config) return;

    const items = filters[apiKey as keyof StaticFilters] || [];
    const hasItems = items.length > 0;

    const options: FilterOption[] = items
      .filter((item: any) => item[config.valueKey] !== null)
      .map((item: any) => ({
        label: item[config.valueKey],
        value: item[config.valueKey],
        count: item.count,
        fieldType: config.fieldType,
      }));

    // Handle null values (e.g., color: null) - display as "Not Specified"
    const nullItem = items.find((item: any) => item[config.valueKey] === null);
    if (nullItem) {
      options.push({
        label: 'not_specified',
        value: 'null',
        count: nullItem.count,
        fieldType: config.fieldType,
        isNotSpecified: true,
      });
    }

    // Find selected value from currentFilters
    const selectedValue = currentFilters[config.paramKey]?.[0];
    const selectedOption = selectedValue
      ? options.find((opt) => String(opt.value) === String(selectedValue))
      : null;

    filterFields.push({
      label: config.label,
      key: config.paramKey,
      type: 'select',
      options,
      value: selectedOption || null,
      disabled: !hasItems,
    });
  });

  return filterFields;
}
